// react
import { useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// fields
import { Select, SelectProps } from '../../../../fields/Select/Select';

// types
import { CompareFn, IfrPlanVersion, Option } from '../../../../../@types';

// context
import { ContextIfrPlan, ContextIfrPlanVersion } from '../../Project.context';
import { useSearchParams } from 'react-router-dom';

export type SelectIfrPlanVersionProps = {
	hasLabel?: boolean;
} & SelectProps;

export const SelectIfrPlanVersion = ({
	label,
	hasLabel = false,
	...props
}: SelectIfrPlanVersionProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { ifrPlan } = useContext(ContextIfrPlan);
	const { ifrPlanVersion, setIfrPlanVersion } = useContext(ContextIfrPlanVersion);

	// options
	let optionsVersions: Option[] = [];

	if (ifrPlan) {
		const sortFnAsc: CompareFn<IfrPlanVersion> = (a, b) => (a.id > b.id ? 1 : -1);
		const sortFnDesc: CompareFn<IfrPlanVersion> = (a, b) => (a.id > b.id ? -1 : 1);
		const versionsFinalized = ifrPlan.versions.filter((v) => v.status === 'FINALIZED');
		const versionsDraft = ifrPlan.versions.filter((v) => v.status === 'DRAFT');

		// define options for finalized versions (ascending order for numbering)
		const optionsVersionsFinalized = versionsFinalized
			.sort(sortFnAsc)
			.map((version, index) => {
				return {
					label: `${t('version')} ${index + 1}`,
					value: version.id,
				};
			})
			.reverse(); // reverse to show newest first in dropdown

		// define options for draft versions
		const optionsVersionsDraft = versionsDraft.sort(sortFnDesc).map((version, index) => {
			return {
				label: `${t('draft')}${index !== 0 ? ` ${index + 1}` : ''}`,
				value: version.id,
			};
		});

		optionsVersions = [...optionsVersionsDraft, ...optionsVersionsFinalized];
	}

	return (
		<Select
			aria-label={hasLabel ? undefined : t('ifrPlanVersion')}
			hasNoneOption={false}
			label={hasLabel ? label || t('ifrPlanVersion') : null}
			value={ifrPlanVersion?.id}
			onChange={(event) => {
				const ifrPlanVersionNew = ifrPlan?.versions.find((v) => v.id === event.target.value);
				if (ifrPlanVersionNew) {
					searchParams.set('ifrPlanVersion', String(ifrPlanVersionNew.id));
					setSearchParams(searchParams);
					setIfrPlanVersion(ifrPlanVersionNew);
				}
			}}
			{...props}
			options={optionsVersions}
		/>
	);
};
